<script setup lang="ts">
import type { AlbumItem } from '~/types/blog'
import { resolveImageAssetUrl } from '~/composables/useAssetUrl'
import { isAppleSafari, resolveAlbumMediaSources, selectAlbumMediaSources } from '~/utils/albumMedia.mjs'

const props = defineProps<{
  item: AlbumItem
  alt: string
}>()

const emit = defineEmits<{
  load: []
  error: []
}>()

const imageLoaded = ref(false)
const imageFailed = ref(false)
const preferOriginalHDR = isAppleSafari(navigator.userAgent, navigator.vendor)
const liveVideoURL = computed(() => resolveImageAssetUrl(props.item.liveVideoUrl || ''))
const mediaSources = computed(() => selectAlbumMediaSources(resolveAlbumMediaSources(props.item), preferOriginalHDR))
const displayURL = computed(() => resolveImageAssetUrl(mediaSources.value.displayURL))
const fallbackURL = computed(() => resolveImageAssetUrl(mediaSources.value.fallbackURL))
const hdrSources = computed(() => mediaSources.value.hdrSources.map(source => ({
  ...source,
  url: resolveImageAssetUrl(source.url)
})).filter(source => Boolean(source.url)))
const isLivePhoto = computed(() => Boolean(liveVideoURL.value))
const useHDRPicture = computed(() => props.item.isHDR && !isLivePhoto.value)
const imageURL = computed(() => isLivePhoto.value
  ? (fallbackURL.value || displayURL.value)
  : (displayURL.value || fallbackURL.value)
)
const aspectRatio = computed(() => {
  const width = Number(props.item.width || 0)
  const height = Number(props.item.height || 0)
  return width > 0 && height > 0 ? `${width} / ${height}` : undefined
})

// handleImageLoad 同步图片状态并通知相册批次完成加载
const handleImageLoad = () => {
  imageLoaded.value = true
  emit('load')
}

// handleImageError 保留当前图片占位，并结束批次中的等待
const handleImageError = () => {
  imageFailed.value = true
  emit('error')
}

// 没有可用预览时直接结束等待，避免向其他浏览器发送 HEIF 原图
watch(imageURL, url => {
  if (!url) handleImageError()
}, { immediate: true })
</script>

<template>
  <div
    class="album-media"
    :class="{ 'is-loaded': imageLoaded, 'is-error': imageFailed }"
    :style="{ aspectRatio: imageLoaded ? undefined : aspectRatio }"
  >
    <BlogLivePhoto
      v-if="isLivePhoto && imageURL"
      :photo-src="imageURL"
      :video-src="liveVideoURL"
      :hdr-sources="item.isHDR ? hdrSources : []"
      :alt="alt"
      @load="handleImageLoad"
      @error="handleImageError"
    >
      <slot />
    </BlogLivePhoto>
    <BlogMediaImage
      v-else-if="imageURL"
      :src="useHDRPicture ? (fallbackURL || displayURL) : imageURL"
      :fallback-src="fallbackURL"
      :sources="useHDRPicture ? hdrSources : []"
      :alt="alt"
      loading="eager"
      :progressive="false"
      @load="handleImageLoad"
      @error="handleImageError"
    />
    <BlogAlbumImageWarning v-if="imageFailed && !imageURL" />
    <slot v-if="!isLivePhoto" />
  </div>
</template>

<style scoped>
.album-media {
  display: block;
  width: 100%;
  position: relative;
  min-height: var(--album-placeholder-height);
  overflow: hidden;
  background: var(--album-skeleton-background);
}

.album-media.is-loaded {
  min-height: 0;
}
</style>
